import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity, Eye, EyeOff, Building2, User, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { registerAgency } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Agency Info
    agencyName: "",
    agencyCode: "",
    agencyEmail: "",
    agencyPhone: "",
    agencyAddress: "",
    // Admin Info
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (!formData.agencyName || !formData.agencyCode || !formData.agencyEmail) {
      toast.error("Please fill in all required fields");
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.adminPassword !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.adminPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    const result = await registerAgency({
      name: formData.agencyName,
      code: formData.agencyCode,
      email: formData.agencyEmail,
      phone: formData.agencyPhone,
      address: formData.agencyAddress,
      admin_name: formData.adminName,
      admin_email: formData.adminEmail,
      admin_password: formData.adminPassword,
    });

    if (result.success) {
      toast.success("Agency registered successfully!");
      // New agencies need to subscribe first - redirect to subscription page
      navigate("/subscribe");
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img 
              src="/carepilot-logo.png" 
              alt="CarePilot" 
              className="h-40 w-auto"
            />
          </Link>

          <h1 className="text-3xl font-bold text-slate-900">Register Your Agency</h1>
          <p className="mt-2 text-slate-600">
            Set up your home care agency in minutes
          </p>

          {/* Progress Steps */}
          <div className="mt-6 flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? "text-primary" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? "bg-primary text-white" : "bg-slate-200"}`}>
                1
              </div>
              <span className="text-sm font-medium">Agency Info</span>
            </div>
            <div className="flex-1 h-px bg-slate-200"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? "text-primary" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? "bg-primary text-white" : "bg-slate-200"}`}>
                2
              </div>
              <span className="text-sm font-medium">Admin Account</span>
            </div>
          </div>

          {step === 1 && (
            <form onSubmit={handleNextStep} className="mt-8 space-y-5">
              {/* Agency Name */}
              <div className="space-y-2">
                <Label htmlFor="agencyName">Agency Name *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="agencyName"
                    name="agencyName"
                    type="text"
                    placeholder="Your Home Care Agency"
                    value={formData.agencyName}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="register-agency-name"
                  />
                </div>
              </div>

              {/* Agency Code */}
              <div className="space-y-2">
                <Label htmlFor="agencyCode">Agency Code *</Label>
                <Input
                  id="agencyCode"
                  name="agencyCode"
                  type="text"
                  placeholder="unique-code (used for login)"
                  value={formData.agencyCode}
                  onChange={handleChange}
                  required
                  data-testid="register-agency-code"
                />
                <p className="text-xs text-slate-500">
                  Employees will use this code to login to your agency
                </p>
              </div>

              {/* Agency Email */}
              <div className="space-y-2">
                <Label htmlFor="agencyEmail">Agency Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="agencyEmail"
                    name="agencyEmail"
                    type="email"
                    placeholder="contact@youragency.com"
                    value={formData.agencyEmail}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="register-agency-email"
                  />
                </div>
              </div>

              {/* Agency Phone */}
              <div className="space-y-2">
                <Label htmlFor="agencyPhone">Agency Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="agencyPhone"
                    name="agencyPhone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.agencyPhone}
                    onChange={handleChange}
                    className="pl-10"
                    data-testid="register-agency-phone"
                  />
                </div>
              </div>

              {/* Agency Address */}
              <div className="space-y-2">
                <Label htmlFor="agencyAddress">Agency Address</Label>
                <Input
                  id="agencyAddress"
                  name="agencyAddress"
                  type="text"
                  placeholder="123 Main St, City, State ZIP"
                  value={formData.agencyAddress}
                  onChange={handleChange}
                  data-testid="register-agency-address"
                />
              </div>

              <Button type="submit" className="w-full" data-testid="register-next-btn">
                Continue to Admin Setup
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              {/* Admin Name */}
              <div className="space-y-2">
                <Label htmlFor="adminName">Your Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="adminName"
                    name="adminName"
                    type="text"
                    placeholder="John Doe"
                    value={formData.adminName}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="register-admin-name"
                  />
                </div>
              </div>

              {/* Admin Email */}
              <div className="space-y-2">
                <Label htmlFor="adminEmail">Your Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="adminEmail"
                    name="adminEmail"
                    type="email"
                    placeholder="admin@youragency.com"
                    value={formData.adminEmail}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="register-admin-email"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="adminPassword">Password *</Label>
                <div className="relative">
                  <Input
                    id="adminPassword"
                    name="adminPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 6 characters"
                    value={formData.adminPassword}
                    onChange={handleChange}
                    required
                    data-testid="register-admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  data-testid="register-confirm-password"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner"></span>
                      Creating...
                    </span>
                  ) : (
                    "Create Agency"
                  )}
                </Button>
              </div>
            </form>
          )}

          <p className="mt-8 text-center text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 relative bg-primary">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20"></div>
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white text-center">
          <h2 className="text-4xl font-bold max-w-md">
            Welcome to CarePilot
          </h2>
          <p className="mt-4 text-lg text-white/80 max-w-md">
            The trusted platform for home care agencies nationwide.
          </p>
        </div>
      </div>
    </div>
  );
}
